import { useCallback, useEffect, useMemo, useState } from 'react'

import { useLocation } from 'react-router-dom'

import api, { jsonOptions } from 'common/api'
import useCanonicalLink from 'common/canonical-link'
import { COBRAND_ALIAS, SOURCES } from 'common/coalition-sources'
import { loginSuccessData, performLogin, useLogin } from 'common/login-service'
import SignInForm from 'components/SignIn'
import { ChallengeData, SignInFormValues, SignInStep } from 'components/SignIn/SignInForm.types'
import TwoFactorForm from 'components/SignIn/TwoFactorForm'

const useQuery = () => {
  const { search } = useLocation()
  return useMemo(() => {
    const params = new URLSearchParams(search)
    return {
      scid: parseInt(params.get('scid') || '', 10),
      source: params.get('source') as 'C' | 'CM',
      errorCode: params.get('errorCode') ?? undefined,
    }
  }, [search])
}

export const SignInRoute = () => {
  const { source, scid, errorCode } = useQuery()
  const [currentStep, setCurrentStep] = useState<SignInStep>(SignInStep.Credentials)
  const [challengeData, setChallengeData] = useState<ChallengeData | undefined>()
  const [params, setParams] = useState('')
  const [expiredError, setExpiredError] = useState<string | undefined>()
  const [passwordChangeRequired, setPasswordChangeRequired] = useState(false)
  const { set: setCanonicalLink, clear: clearCanonicalLink } = useCanonicalLink()
  useEffect(() => {
    setCanonicalLink()
    return () => {
      clearCanonicalLink()
    }
  }, [setCanonicalLink, clearCanonicalLink])

  useEffect(() => {
    const params: { [k: string]: any } = {}
    if (scid) {
      params.scid = scid
    }
    if (source) {
      params.source = COBRAND_ALIAS[source]
      if (!SOURCES.includes(COBRAND_ALIAS[source])) {
        setParams('')
      } else {
        setParams(`?${new URLSearchParams(params).toString()}`)
      }
    }
  }, [source, scid])

  const handleChallengeResponse = useCallback((data?: ChallengeData) => {
    if (data) {
      setChallengeData(data)
      setCurrentStep(SignInStep.TwoFactor)
    }
  }, [])

  const handlePasswordChangeRequired = useCallback(() => {
    setPasswordChangeRequired(true)
  }, [])

  const login = useLogin(undefined, handleChallengeResponse, handlePasswordChangeRequired)

  const handleCredentialsSubmit = async (values: SignInFormValues) => {
    await login(values.email, values.password, params)
  }

  const handleTwoFactorSubmit = async (code: string) => {
    if (!challengeData) return
    const response = await api.post<
      { ChallengeCode?: string; UserId?: string; ChallengeId?: string },
      loginSuccessData | undefined
    >(
      `/api/login/challenge${params ?? ''}`,
      {
        UserId: challengeData.UserId,
        ChallengeCode: code,
        ChallengeId: challengeData.ChallengeId,
      },
      jsonOptions,
    )
    if (response && response.UserTypes && response.UserTypes.length > 0) {
      performLogin(response, {}, undefined, undefined)
    } else {
      throw new Error('This account does not have any active roles and cannot login.')
    }
  }

  const handleTwoFactorCancel = (expired?: boolean) => {
    setChallengeData(undefined)
    setCurrentStep(SignInStep.Credentials)
    if (expired) {
      setExpiredError('Your two-factor code has expired, please sign in again.')
    }
  }

  const handleResendCode = async () => {
    if (!challengeData) return

    await api.post(
      `/api/login/challenge/resend${params ?? ''}`,
      {
        UserId: challengeData.UserId,
        ChallengeId: challengeData.ChallengeId,
      },
      jsonOptions,
    )
  }

  if (currentStep === SignInStep.TwoFactor && challengeData) {
    return (
      <TwoFactorForm
        challengeData={challengeData}
        onSubmit={handleTwoFactorSubmit}
        onCancel={handleTwoFactorCancel}
        onResendCode={handleResendCode}
      />
    )
  }

  return (
    <SignInForm
      onSubmit={handleCredentialsSubmit}
      scid={scid}
      source={source}
      errorCode={errorCode}
      expiredError={expiredError}
      passwordChangeRequired={passwordChangeRequired}
    />
  )
}

export default SignInRoute
